<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useBookingStore } from "@stores/BookingStore";
import { useField } from "vee-validate";
import * as yup from "yup";
import FieldInputNumber from "@components/FieldInputNumber.vue";

// Define component props
const props = defineProps({
  formName: String,
  name: String,
  label: String,
  placeholder: String,
  description: String,
  modelValue: Object,
  errorPosition: {
    type: String,
    default: "static",
  },
  type: {
    type: String,
    default: "text",
  },
  required: {
    type: Boolean,
    default: true,
  },
});

const bookingStore = useBookingStore();
const field = props.name;
const tempValue = ref({});

const { value, errorMessage, validate } = useField(
  "patients",
  yup.object({
    total: yup
      .number()
      .required("Patients required")
      .moreThan(0, "At least one patient required"),
  }),
  { initialValue: props.modelValue.total ? props.modelValue : {} }
);

// if (props.modelValue.total > 0) {
//   tempValue.value = {
//     adult65: props.modelValue.adult65 || 0,
//     adult16: props.modelValue.adult16 || 0,
//     youngAdult: props.modelValue.youngAdult || 0,
//     children: props.modelValue.children || 0,
//     infant: props.modelValue.infant || 0,
//   };
// }

// const patients = ref([
//   {
//     id: 1,
//     name: "adult65",
//     displayName: "Adult (65+ years)",
//     value: props.modelValue.adult65 || 0,
//   },
//   {
//     id: 2,
//     name: "Adult (16+)",
//     displayName: "Adult (16+)",
//     value: props.modelValue.adult65 || 0,
//   },
//   {
//     id: 3,
//     name: "youngAdult",
//     displayName: "Young Adult (12-15)",
//     value: props.modelValue.adult65 || 0,
//   },
//   {
//     id: 4,
//     name: "children",
//     displayName: "Children (2-11)",
//     value: props.modelValue.adult65 || 0,
//   },
//   {
//     id: 5,
//     name: "infant",
//     displayName: "Infant (under 2)",
//     value: props.modelValue.adult65 || 0,
//   },
// ]);

const patients = ref({
  adult65: {
    id: 1,
    name: "Adult (65+ years)",
    count: props.modelValue.adult65.count || 0,
  },
  adult16: {
    id: 2,
    name: "Adult (16+)",
    count: props.modelValue.adult16.count || 0,
  },
  youngAdult: {
    id: 3,
    name: "Young Adult (12-15)",
    count: props.modelValue.youngAdult.count || 0,
  },
  children: {
    id: 4,
    name: "Children (2-11)",
    count: props.modelValue.children.count || 0,
  },
  infant: {
    id: 5,
    name: "Infant (under 2)",
    count: props.modelValue.infant.count || 0,
  },
});

const dropDownEnabled = ref(false);
// const initValueRanOnce = ref(false);

// Refs for DOM elements
const inputRef = ref(null);
const dropdownRef = ref(null);

// Computed property for placeholder text
const placeholderText = computed(() =>
  Array.isArray(value.value) && value.value.length ? "" : props.placeholder
);

// Function to toggle dropdown visibility
const toggleDropdown = () => {
  dropDownEnabled.value = !dropDownEnabled.value;
};

// Define emits to declare event emission capability
const emit = defineEmits(["update:modelValue"]);

watch(value, (newValue) => {
  emit("update:modelValue", newValue);
});

watch(
  patients.value,
  (newVal) => {
    // Ensure all individual counts are numbers before summing
    const adult65 = Number(newVal?.adult65.count) || 0;
    const adult16 = Number(newVal?.adult16.count) || 0;
    const youngAdult = Number(newVal?.youngAdult.count) || 0;
    const childrenCount = Number(newVal?.children.count) || 0;
    const infantCount = Number(newVal?.infant.count) || 0;

    value.value.total =
      adult65 + adult16 + youngAdult + childrenCount + infantCount;
    value.value.totalChildren = childrenCount + infantCount;
    value.value.totalAdults = adult65 + adult16 + youngAdult;

    const mergedObj = { ...value.value, ...newVal };
    value.value = mergedObj;
  },
  { deep: true }
);

// Lifecycle hook for adding event listeners on mount
onMounted(() => {
  // Handle click events on the entire document
  const handleDocumentClick = (event) => {
    if (
      inputRef.value &&
      dropdownRef.value &&
      !inputRef.value.contains(event.target) &&
      !dropdownRef.value.contains(event.target)
    ) {
      dropDownEnabled.value = false;
    }
  };

  document.addEventListener("click", handleDocumentClick);

  // Cleanup event listeners on component unmount
  onBeforeUnmount(() => {
    document.removeEventListener("click", handleDocumentClick);
  });
});
</script>

<template>
  <fieldset class="relative frm-field">
    <label :for="name" class="block mb-10">
      {{ label }}
    </label>
    <div class="flex gap-2 frm-input 0">
      <i class="ri-group-fill"></i>
      <input
        ref="inputRef"
        class="w-full"
        :id="name"
        v-model="value.total"
        :name="name"
        :placeholder="placeholderText"
        type="text"
        @blur="validate"
        @click="toggleDropdown"
        readonly />
    </div>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none"
      :class="{ 'md:absolute': errorPosition === 'absolute' }">
      {{ errorMessage }}
    </span>
    <span v-if="description" class="frm-desc">
      {{ description }}
    </span>

    <div
      v-show="dropDownEnabled"
      ref="dropdownRef"
      class="z-20 absolute flex flex-col gap-6 bg-white mt-4 p-20 border border-gray-border rounded-3xl w-full">
      <FieldInputNumber
        v-model="patients.adult65.count"
        name="no_adults_65"
        label="65+" />
      <FieldInputNumber
        v-model="patients.adult16.count"
        name="no_adults_16"
        label="Adults (16+)" />
      <FieldInputNumber
        v-model="patients.youngAdult.count"
        name="young_adults"
        label="Young Adults (12-15)" />
      <FieldInputNumber
        v-model="patients.children.count"
        name="children"
        label="Children (2-11)" />
      <FieldInputNumber
        v-model="patients.infant.count"
        name="infant"
        label="Infant (under 2)" />
    </div>
  </fieldset>
</template>
